import { Link } from "react-router-dom";

import { PageContainer } from "@/components/core/page-container";
import { useLocale, useT } from "@/i18n/locale-context";
import { cn } from "@/lib/utils";
import type { FooterGroup, SocialLink } from "@/types/site";

interface FooterProps {
  compact?: boolean;
  groups: FooterGroup[];
  siteName: string;
  socialLinks: SocialLink[];
  tagline: string;
}

export function Footer({
  compact = false,
  groups,
  siteName,
  socialLinks,
  tagline,
}: FooterProps): JSX.Element {
  const { localePath } = useLocale();
  const t = useT();

  return (
    <footer className="border-t border-border/70 bg-background">
      <PageContainer
        className={cn(
          "grid gap-8 py-10 sm:gap-10 sm:py-12 md:py-14",
          compact
            ? "lg:grid-cols-[1.1fr,1.9fr]"
            : "lg:grid-cols-[1.05fr,1.95fr]",
        )}
      >
        <div className={cn("space-y-5", compact ? "max-w-md" : "max-w-lg")}>
          <Link
            className="text-[0.9rem] font-semibold uppercase tracking-[0.22em] text-primary transition-opacity duration-300 hover:opacity-75 sm:text-[1.05rem] sm:tracking-[0.26em]"
            to={localePath("/")}
          >
            {siteName}
          </Link>
          <p className="text-sm leading-7 text-muted-foreground sm:text-base">
            {tagline}
          </p>
        </div>
        <div
          className={cn(
            "grid gap-8 border-t border-border/70 pt-6 lg:border-t-0 lg:pt-0",
            socialLinks.length > 0
              ? "sm:grid-cols-2 lg:grid-cols-3"
              : "sm:grid-cols-2",
          )}
        >
          {groups.map((group) => (
            <div key={group.title} className="space-y-4">
              <h2 className="text-xs font-semibold uppercase tracking-[0.3em] text-foreground/58">
                {group.title}
              </h2>
              <ul className="space-y-3 text-[1rem] text-muted-foreground">
                {group.links.map((link) => (
                  <li key={link.href}>
                    <Link
                      className="inline-flex transition-[transform,color] duration-300 motion-safe:hover:translate-x-1 hover:text-primary"
                      to={localePath(link.href)}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {socialLinks.length > 0 ? (
            <div className="space-y-4">
              <h2 className="text-xs font-semibold uppercase tracking-[0.3em] text-foreground/58">
                {t("footer.follow")}
              </h2>
              <ul className="space-y-3 text-[1rem] text-muted-foreground">
                {socialLinks.map((link) => (
                  <li key={link.href}>
                    <a
                      className="inline-flex transition-[transform,color] duration-300 motion-safe:hover:translate-x-1 hover:text-primary"
                      href={link.href}
                      rel="noreferrer"
                      target="_blank"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </PageContainer>
      <div className="border-t border-white/10 bg-[#1c1c1c]">
        <PageContainer>
          <a
            className="flex flex-col gap-3 py-5 text-white transition-[transform,opacity] duration-300 hover:opacity-90 motion-safe:hover:-translate-y-0.5 md:flex-row md:items-center md:justify-between"
            href="https://apoloinv.com"
            rel="noreferrer"
            target="_blank"
          >
            <img
              alt={t("footer.apoloLogoAlt")}
              className="h-8 w-auto max-w-[10rem] sm:max-w-[12rem]"
              decoding="async"
              height="32"
              loading="lazy"
              src="/ApoloBranding/ApoloInv-Banner.svg"
              width="192"
            />
            <p className="max-w-sm text-sm font-medium leading-6 text-white md:text-right">
              {t("footer.credit")}
            </p>
          </a>
        </PageContainer>
      </div>
    </footer>
  );
}
